import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Typography,
  Switch,
  Button,
  Radio,
  Divider,
  Alert,
  Tooltip,
} from "antd";
import { FormInput } from "../inputs";
import { currencyMapper } from "../../config/helper";

const { Title, Text } = Typography;

const AmountNTenure = ({ next }) => {
  const { handleSubmit, control, watch, setValue, register } = useForm();
  const [higherReturns, setHigherReturns] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const plans = [
    { rate: "8.84%", tenure: "18 months", payout: "On Maturity" },
    { rate: "11.42%", tenure: "60 months", payout: "On Maturity" },
    { rate: "9.19%", tenure: "24 months", payout: "On Maturity" },
  ];
  // Tenure array
  const tenures = [12, 15, 18, 24, 30, 36, 42, 50, 60];

  const onSubmit = (data) => {
    console.log("Form Data:", data);
    next();
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      style={{
        padding: "20px",
        maxWidth: "600px",
        borderRadius: "8px",
      }}
    >
      {/* Greeting Section */}
      <Title level={3}>
        Hello, <Text strong>Girish</Text>
      </Title>
      <Text type="secondary">Transaction No</Text>
      <br />
      <Text strong>ZENU5345678986</Text>

      {/* Instructions */}
      <Text style={{ display: "block", marginTop: "16px" }}>
        Kindly select a FD plan below. You would also verify your PAN and Aadhar
        details and add some additional information to complete booking your FD
        online.
      </Text>

      <Divider />

      {/* Senior Citizen and Woman Depositor Switches */}
      <div className="d-flex">
        <div className="d-flex align-center">
          <Controller
            name="isSeniorCitizen"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <Switch
                {...field}
                checked={field.value}
                onChange={(checked) => setValue("isSeniorCitizen", checked)}
                style={{ marginLeft: "8px", marginRight: "16px" }}
              />
            )}
          />
          <div className="d-flex flex-col">
            <Text>Are you a senior citizen?</Text>
            <Text className="sm">(Get an additional 0.50% interest rate)</Text>
          </div>
        </div>
        <div className="d-flex align-center">
          <Controller
            name="isWomanDepositor"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <Switch
                {...field}
                checked={field.value}
                onChange={(checked) => setValue("isWomanDepositor", checked)}
                style={{ marginLeft: "8px", marginRight: "16px" }}
              />
            )}
          />
          <div className="d-flex flex-col">
            <Text>Are you a woman depositor?</Text>
            <Text className="sm">(Get an additional 0.10% interest rate)</Text>
          </div>
        </div>
      </div>
      <Divider />

      {/* Investment Section */}
      <Text strong>I want to invest (Min $300.00 Max $1,000,000.00)</Text>
      <div className="pp-form-item">
        <FormInput
          prefix={currencyMapper["INR"]}
          control={control}
          {...register("investmentAmount")}
          placeholder={"Enter Investment Amount"}
        />
      </div>

      <div
        style={{
          display: "flex",
          marginBottom: "16px",
          marginTop: "16px",
          gap: "1rem",
        }}
      >
        <Button onClick={() => setValue("investmentAmount", 5000)}>
          ₹5000
        </Button>
        <Button onClick={() => setValue("investmentAmount", 10000)}>
          ₹10,000
        </Button>
        <Button onClick={() => setValue("investmentAmount", 50000)}>
          ₹50,000
        </Button>
      </div>

      <Divider />

      {/* Tenure Selection */}
      <Text strong>Select your tenure (Months)</Text>
      <Controller
        name="tenure"
        control={control}
        defaultValue={12}
        render={({ field }) => (
          <Radio.Group
            value={field.value}
            onChange={field.onChange}
            style={{ marginTop: "8px", gap: "1rem", display: "flex" }}
          >
            {tenures.map((tenure) => (
              <Radio.Button key={tenure} value={tenure}>
                {tenure}
              </Radio.Button>
            ))}
          </Radio.Group>
        )}
      />

      {/* Tenure Alert */}
      <Alert
        message="If you invest for a longer tenure, you will earn a higher interest rate."
        type="info"
        showIcon
        style={{ marginTop: "16px" }}
      />

      <Divider />

      {/* Interest Section */}
      <div className="mb2">
        <Text strong>Interest</Text>
        <Tooltip title="Select how to manage your interest">
          <Text type="secondary" style={{ marginLeft: "8px" }}>
            ⓘ
          </Text>
        </Tooltip>
        <Controller
          name="interestType"
          control={control}
          defaultValue="Reinvest"
          render={({ field }) => (
            <div style={{ marginTop: "8px", display: "flex", gap: "10px" }}>
              <button
                className={field.value === "Reinvest" ? "send-btn" : "back-btn"}
                onClick={() => setValue("interestType", "Reinvest")}
              >
                Reinvest
              </button>
              <button
                className={
                  field.value === "PayInterest" ? "send-btn" : "back-btn"
                }
                onClick={() => setValue("interestType", "PayInterest")}
                style={{ marginLeft: "8px" }}
              >
                Pay Interest
              </button>
            </div>
          )}
        />
      </div>

      {/* Interest Payout Section */}
      <div style={{ marginBottom: "16px" }}>
        <Text strong>Select the Interest Payout</Text>
        <Tooltip title="Choose the interest payout option">
          <Text type="secondary" style={{ marginLeft: "8px" }}>
            ⓘ
          </Text>
        </Tooltip>
        <Controller
          name="interestPayout"
          control={control}
          defaultValue="OnMaturity"
          render={({ field }) => (
            <Radio.Group
              value={field.value}
              onChange={field.onChange}
              style={{ marginTop: "8px", gap: "1rem", display: "flex" }}
            >
              <Radio.Button value="OnMaturity">OnMaturity</Radio.Button>
              <Radio.Button value="Monthly">Monthly</Radio.Button>
              <Radio.Button value="Quarterly">Quarterly</Radio.Button>
              <Radio.Button value="Yearly">Yearly</Radio.Button>
              <Radio.Button value="Half-Yearly">Half-Yearly</Radio.Button>
            </Radio.Group>
          )}
        />
        <Alert
          message="For higher returns on your deposit, choose payout on maturity."
          type="info"
          showIcon
          style={{ marginTop: "8px" }}
        />
      </div>

      <Divider />
      {/* Principal Section */}
      <div style={{ marginBottom: "16px" }}>
        <Text strong>Principal</Text>
        <Tooltip title="What do you want to do with the principal on maturity?">
          <Text type="secondary" style={{ marginLeft: "8px" }}>
            ⓘ
          </Text>
        </Tooltip>
        <Controller
          name="principalOption"
          control={control}
          defaultValue="AutoRenew"
          render={({ field }) => (
            <div style={{ marginTop: "8px" }}>
              <button
                className={
                  field.value === "AutoRenew" ? "send-btn" : "back-btn"
                }
                onClick={() => setValue("principalOption", "AutoRenew")}
              >
                Auto Renew
              </button>
              <button
                className={
                  field.value === "AutoTransfer" ? "send-btn" : "back-btn"
                }
                onClick={() => setValue("principalOption", "AutoTransfer")}
                style={{ marginLeft: "8px" }}
              >
                Auto Transfer
              </button>
            </div>
          )}
        />
      </div>

      <Divider />

      {/* Higher Returns Section */}
      <div style={{ marginBottom: "16px" }}>
        <Switch
          checked={higherReturns}
          onChange={setHigherReturns}
          style={{ marginRight: "8px" }}
        />
        <Text>Get higher returns from your FD?</Text>
      </div>
      {higherReturns && (
        <>
          <div className="recommended-plans-container">
            <h3>
              Choose any of the below recommended plans to get the higher
              returns out of your investment.
            </h3>
            <div className="plans">
              {plans.map((plan, index) => (
                <>
                  <div
                    key={index}
                    className={`ant-card ant-card-bordered plan-box ${
                      selectedPlan === index ? "selected" : ""
                    }`}
                    onClick={() => setSelectedPlan(index)}
                  >
                    <p className="rate">{plan.rate}</p>
                    <p className="tenure">
                      {plan.tenure} {plan.payout}
                    </p>
                  </div>
                  <p className="yield">*Effective Yield</p>
                </>
              ))}
            </div>
          </div>
          <Divider />
        </>
      )}

      {/* Action Buttons */}
      <div className="action-btns">
        <button className="send-btn" type="submit">
          Book your FD
        </button>
        <button type="button" className="back-btn">
          Cancel
        </button>
      </div>
    </form>
  );
};

export default AmountNTenure;
